import React, { useState, useEffect } from 'react';
import { X, Loader2, Store, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const ShopifyConnectModal = ({ isOpen, onClose, brand, onSuccess }) => {
    const { currentUser } = useAuth();
    const [shopDomain, setShopDomain] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [step, setStep] = useState('input'); // 'input' | 'connecting' | 'success'

    const handleConnect = async () => {
        if (!shopDomain.trim()) {
            setError('Please enter your Shopify store domain');
            return;
        }

        if (!currentUser || !brand) {
            setError('Missing user or brand information');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setStep('connecting');

            // Request OAuth URL from backend
            const response = await fetch('http://localhost:3001/api/shopify/connect', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    shopDomain: shopDomain.trim(),
                    brandId: brand.id,
                    userId: currentUser.uid
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to initiate connection');
            }

            const { authUrl } = await response.json();

            // Open OAuth flow in popup
            const width = 600;
            const height = 700;
            const left = (window.screen.width / 2) - (width / 2);
            const top = (window.screen.height / 2) - (height / 2);

            const popup = window.open(
                authUrl,
                'shopify-oauth',
                `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
            );

            // Listen for OAuth completion
            const handleMessage = async (event) => {
                if (event.data.type === 'oauth-success' && event.data.platform === 'shopify') {
                    console.log('Shopify OAuth successful:', event.data);
                    window.removeEventListener('message', handleMessage);
                    setStep('success');
                    setLoading(false);

                    // Notify parent of success after brief delay
                    setTimeout(() => {
                        if (onSuccess) onSuccess(event.data.connectionId);
                        onClose(true);
                    }, 1500);

                } else if (event.data.type === 'oauth-error') {
                    console.error('Shopify OAuth error:', event.data.error);
                    setError(event.data.error);
                    setStep('input');
                    setLoading(false);
                    window.removeEventListener('message', handleMessage);
                }
            };

            window.addEventListener('message', handleMessage);

            // Check if popup was blocked
            if (!popup || popup.closed || typeof popup.closed === 'undefined') {
                throw new Error('Popup was blocked. Please allow popups for this site.');
            }

            // Monitor popup closure
            const checkClosed = setInterval(() => {
                try {
                    if (popup.closed) {
                        clearInterval(checkClosed);
                        window.removeEventListener('message', handleMessage);
                        if (step === 'connecting') {
                            setStep('input');
                            setLoading(false);
                        }
                    }
                } catch (e) {
                    clearInterval(checkClosed);
                }
            }, 500);

        } catch (err) {
            console.error('Shopify connection error:', err);
            setError(err.message);
            setStep('input');
            setLoading(false);
        }
    };

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setError(null);
            setLoading(false);
            setStep('input');
            setShopDomain('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(4px)'
            }}
            onClick={() => step === 'input' && onClose()}
        >
            <div
                className="card"
                style={{
                    maxWidth: '480px',
                    width: '90%',
                    position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--space-6)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: 'var(--radius-lg)',
                            background: 'linear-gradient(135deg, #96bf48 0%, #5c8a2f 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Store size={24} color="white" />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, marginBottom: 'var(--space-1)' }}>
                                Connect Shopify Store
                            </h2>
                            <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                {brand?.name}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--space-2)' }}
                        disabled={loading}
                    >
                        <X size={20} />
                    </button>
                </div>

                {step === 'success' ? (
                    <div style={{
                        textAlign: 'center',
                        padding: 'var(--space-8)'
                    }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '50%',
                            background: 'var(--color-success-bg)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto var(--space-4)'
                        }}>
                            <CheckCircle size={32} color="var(--color-success)" />
                        </div>
                        <h3 style={{ margin: 0, marginBottom: 'var(--space-2)' }}>
                            Store Connected!
                        </h3>
                        <p className="text-secondary" style={{ margin: 0 }}>
                            Your Shopify store has been successfully connected.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Error Message */}
                        {error && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    background: 'var(--color-error-bg)',
                                    color: 'var(--color-error)',
                                    padding: 'var(--space-3)',
                                    borderRadius: 'var(--radius-md)',
                                    marginBottom: 'var(--space-4)',
                                    border: '1px solid var(--color-error)',
                                    fontSize: 'var(--font-size-sm)'
                                }}
                            >
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}

                        {/* Shop Domain Input */}
                        <div style={{ marginBottom: 'var(--space-6)' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: 'var(--space-2)',
                                fontWeight: 500
                            }}>
                                Shopify Store URL
                            </label>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                border: '2px solid var(--color-border)',
                                borderRadius: 'var(--radius-lg)',
                                overflow: 'hidden',
                                background: 'var(--color-bg-secondary)'
                            }}>
                                <input
                                    type="text"
                                    value={shopDomain}
                                    onChange={(e) => setShopDomain(e.target.value)}
                                    placeholder="mystore"
                                    disabled={loading}
                                    style={{
                                        flex: 1,
                                        border: 'none',
                                        padding: 'var(--space-3) var(--space-4)',
                                        fontSize: 'var(--font-size-md)',
                                        background: 'transparent',
                                        outline: 'none',
                                        color: 'var(--color-text-primary)'
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && !loading) {
                                            handleConnect();
                                        }
                                    }}
                                />
                                <span style={{
                                    padding: 'var(--space-3) var(--space-4)',
                                    background: 'var(--color-bg-tertiary)',
                                    color: 'var(--color-text-secondary)',
                                    borderLeft: '1px solid var(--color-border)',
                                    fontSize: 'var(--font-size-sm)'
                                }}>
                                    .myshopify.com
                                </span>
                            </div>
                            <p className="text-secondary" style={{
                                margin: 0,
                                marginTop: 'var(--space-2)',
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                Enter your store name from your Shopify admin URL
                            </p>
                        </div>

                        {/* Connect Button */}
                        <button
                            className="btn btn-primary"
                            onClick={handleConnect}
                            disabled={loading || !shopDomain.trim()}
                            style={{
                                width: '100%',
                                padding: 'var(--space-3) var(--space-4)',
                                background: 'linear-gradient(135deg, #96bf48 0%, #5c8a2f 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 'var(--space-2)'
                            }}
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={18} className="spinning" />
                                    Connecting...
                                </>
                            ) : (
                                <>
                                    <Store size={18} />
                                    Connect Store
                                </>
                            )}
                        </button>

                        {/* Info */}
                        <div
                            style={{
                                marginTop: 'var(--space-6)',
                                padding: 'var(--space-4)',
                                background: 'var(--color-bg-tertiary)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-border)'
                            }}
                        >
                            <p className="text-secondary" style={{
                                margin: 0,
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                We'll request access to read and update your products, themes, and analytics data for CRO optimization. Your credentials are encrypted and securely stored.
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ShopifyConnectModal;
